<!-- W4-6 B 端纠纷/投诉只读（ADR-021 / L18-032） -->
<template>
  <div>
    <el-alert :closable="false" class="mb-16px" show-icon type="info">
      本页为<strong>只读</strong>：机构可查看与本机构相关的投诉/纠纷进度与仲裁结果，不可进行仲裁或关闭操作。
    </el-alert>

    <el-card shadow="never">
      <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="80px">
        <el-form-item label="工单号" prop="disputeNo">
          <el-input
            v-model="queryParams.disputeNo"
            class="!w-180px"
            clearable
            placeholder="工单号"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="订单 ID" prop="orderId">
          <el-input
            v-model="queryParams.orderId"
            class="!w-140px"
            clearable
            placeholder="订单 ID"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item label="纠纷类型" prop="type">
          <el-select v-model="queryParams.type" class="!w-140px" clearable placeholder="全部">
            <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="queryParams.status" class="!w-140px" clearable placeholder="全部">
            <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery">
            <Icon class="mr-5px" icon="ep:search" />搜索
          </el-button>
          <el-button @click="resetQuery">
            <Icon class="mr-5px" icon="ep:refresh" />重置
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="list" stripe>
        <el-table-column align="center" label="ID" prop="id" width="72" />
        <el-table-column align="center" label="工单号" min-width="150" prop="disputeNo" />
        <el-table-column align="center" label="订单号" min-width="140" prop="orderNo" />
        <el-table-column align="center" label="类型" prop="type" width="100">
          <template #default="{ row }">{{ typeLabel(row.type) }}</template>
        </el-table-column>
        <el-table-column label="投诉原因" min-width="160" prop="reason" show-overflow-tooltip />
        <el-table-column align="center" label="用户" prop="userNickname" width="100" />
        <el-table-column align="center" label="师傅" prop="providerName" width="100" />
        <el-table-column align="center" label="状态" prop="status" width="100">
          <template #default="{ row }">
            <el-tag :type="statusTagType(row.status)" size="small">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column align="center" label="创建时间" prop="createTime" width="170" />
        <el-table-column align="center" fixed="right" label="操作" width="80">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row.id)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <Pagination
        v-model:limit="queryParams.pageSize"
        v-model:page="queryParams.pageNo"
        :total="total"
        @pagination="getList"
      />
    </el-card>

    <el-dialog v-model="detailVisible" title="纠纷详情（只读）" width="720px">
      <el-descriptions v-if="detail" :column="2" border>
        <el-descriptions-item label="工单号">{{ detail.disputeNo }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ statusLabel(detail.status) }}</el-descriptions-item>
        <el-descriptions-item label="订单号">{{ detail.orderNo }}</el-descriptions-item>
        <el-descriptions-item label="订单 ID">{{ detail.orderId }}</el-descriptions-item>
        <el-descriptions-item label="纠纷类型">{{ typeLabel(detail.type) }}</el-descriptions-item>
        <el-descriptions-item label="用户">{{ detail.userNickname || detail.userId }}</el-descriptions-item>
        <el-descriptions-item label="师傅">{{ detail.providerName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="投诉原因" :span="2">{{ detail.reason }}</el-descriptions-item>
        <el-descriptions-item label="详细描述" :span="2">{{ detail.description || '—' }}</el-descriptions-item>
        <el-descriptions-item label="师傅答辩" :span="2">{{ detail.providerResponse || '（未答辩）' }}</el-descriptions-item>
        <el-descriptions-item label="答辩截止">{{ detail.respondDeadline || '—' }}</el-descriptions-item>
        <el-descriptions-item label="仲裁结果">{{ verdictLabel(detail.verdict) || '—' }}</el-descriptions-item>
        <el-descriptions-item label="裁定退款">
          {{ detail.refundAmount != null ? `¥${detail.refundAmount}` : '—' }}
        </el-descriptions-item>
        <el-descriptions-item label="仲裁说明" :span="2">{{ detail.verdictRemark || '—' }}</el-descriptions-item>
        <el-descriptions-item v-if="detail.evidenceUrls?.length" label="证据" :span="2">
          <el-image
            v-for="(url, idx) in detail.evidenceUrls"
            :key="idx"
            :src="url"
            :preview-src-list="detail.evidenceUrls"
            class="mr-8px h-60px w-60px"
            preview-teleported
          />
        </el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="detailVisible = false">关 闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance } from 'element-plus'
import {
  getO2oMerchantDispute,
  getO2oMerchantDisputePage,
  type O2oDisputeMerchantVO
} from '@/api/merchant/o2o/dispute'

defineOptions({ name: 'MerchantO2oDispute' })

const typeOptions = [
  { label: '投诉', value: 'COMPLAINT' },
  { label: '退款争议', value: 'REFUND' },
  { label: '报损', value: 'DAMAGE' }
]
const statusOptions = [
  { label: '待答辩', value: 'PENDING_RESPOND' },
  { label: '仲裁中', value: 'ARBITRATING' },
  { label: '已结案', value: 'RESOLVED' },
  { label: '已关闭', value: 'CLOSED' }
]
const verdictOptions = [
  { label: '用户胜诉', value: 'USER_WIN' },
  { label: '师傅胜诉', value: 'PROVIDER_WIN' },
  { label: '部分支持', value: 'PARTIAL' },
  { label: '驳回投诉', value: 'REJECTED' }
]

const typeLabel = (v?: string) => typeOptions.find((x) => x.value === v)?.label || v || '—'
const statusLabel = (v?: string) => statusOptions.find((x) => x.value === v)?.label || v || '—'
const verdictLabel = (v?: string) => verdictOptions.find((x) => x.value === v)?.label || v || ''
const statusTagType = (v?: string) =>
  ({ PENDING_RESPOND: 'warning', ARBITRATING: 'primary', RESOLVED: 'success', CLOSED: 'info' }[v || ''] ||
  'info')

const loading = ref(false)
const list = ref<O2oDisputeMerchantVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  disputeNo: undefined as string | undefined,
  orderId: undefined as string | undefined,
  type: undefined as string | undefined,
  status: undefined as string | undefined
})
const queryFormRef = ref<FormInstance>()

const detailVisible = ref(false)
const detail = ref<O2oDisputeMerchantVO | null>(null)

async function getList() {
  loading.value = true
  try {
    const params: any = { ...queryParams }
    if (params.orderId) params.orderId = Number(params.orderId)
    const data = await getO2oMerchantDisputePage(params)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

function handleQuery() {
  queryParams.pageNo = 1
  getList()
}

function resetQuery() {
  queryFormRef.value?.resetFields()
  handleQuery()
}

async function openDetail(id: number) {
  detail.value = await getO2oMerchantDispute(id)
  detailVisible.value = true
}

onMounted(() => {
  getList()
})
</script>
